import { NgIf, NgFor, AsyncPipe, LowerCasePipe, DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemIcon,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
  MatDivider,
} from '@angular/material/list';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { WrapperSiteSidenavActions } from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';

import { Order } from '../../models/cycle.model';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { OrderItemDialogComponent } from '../order-item-dialog/order-item-dialog.component';
import * as cycleActions from './../../store/actions/cycle.actions';

@Component({
  selector: 'app-order-overview',
  templateUrl: 'order-overview.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    MatNavList,
    NgFor,
    MatListItem,
    MatIcon,
    MatListItemIcon,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatIconButton,
    MatListItemMeta,
    MatDivider,
    AsyncPipe,
    LowerCasePipe,
    DatePipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class OrderOverviewComponent {
  readonly cycle$ = this.store.select(getSelectedCycle);
  readonly isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  constructor(
    private readonly store: Store<RootState>,
    private readonly router: Router,
    @Inject(BreakpointServiceInjector)
    private readonly breakpoint: BreakpointService,
    private readonly dialog: DialogService,
    private readonly localeService: LocaleService,
  ) {
    this.store.dispatch(cycleActions.loadPropagationCompaniesRequest());
  }

  add() {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      this.router.navigate(['/cycle', cycle.id, 'orders', 'add']);
      this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
        if (isMobile) {
          this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
        }
      });
    });
  }

  addDialog() {
    this.store.dispatch(
      cycleActions.setSelectedOrderId({
        id: undefined,
      }),
    );
    this.cycle$
      .pipe(
        skipWhile((c) => !c?.id),
        take(1),
      )
      .subscribe((cycle) => {
        this.dialog
          .form(OrderItemDialogComponent, {
            confirmButton: true,
            confirmButtonText: this.localeService.translate('add'),
            pushHistoryState: false,
            orderId: null,
          })
          .subscribe((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              const nextOrderId =
                cycle.orders && cycle.orders.length > 0
                  ? Math.max(...cycle.orders.map((d) => d.id)) + 1
                  : 1;
              this.store.dispatch(
                cycleActions.addOrder({
                  cycleId: cycle.id,
                  order: { ...result.result.order, id: nextOrderId },
                }),
              );
            }
          });
      });
  }

  openOrderEdit(orderId: number) {
    this.store.dispatch(
      cycleActions.setSelectedOrderId({
        id: orderId,
      }),
    );
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      this.dialog
        .form(OrderItemDialogComponent, {
          confirmButton: true,
          confirmButtonText: this.localeService.translate('save'),
          pushHistoryState: false,
          orderId,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(
              cycleActions.editOrder({ cycleId: cycle.id, order: result.result.order }),
            );
          }
          this.store.dispatch(
            cycleActions.setSelectedOrderId({
              id: undefined,
            }),
          );
        });
    });
  }

  delete(event: Event, order: Order) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      if (order && order.id) {
        this.store.dispatch(
          cycleActions.deleteOrderWithConfirmation({ cycleId: cycle.id, id: order.id }),
        );
      }
    });
  }

  sort(orders: Array<Order>): Array<Order> {
    return orders.slice().sort((a, b) => (a.id < b.id ? -1 : 0));
  }
}
